import { useContentMotion } from "../lib/useContentMotion";
import { SettingsTabs, settingsScope as readSettingsScope, type SettingsScope } from "./SettingsTabs";
import { navigateWorkbench } from "../lib/studioNavigation";
import { workspaceHref } from "../lib/workspaces";
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError, api, type CapabilityEntry, type PanelEntry } from "../lib/api";
import { toolIndex } from "../studio/ask";
import { Icon } from "../components/Icon";
import { AgentDeletion } from "../hub/AgentDeletion";
import { Accordion } from "../components/rumonai";
import { settingsFeed } from "../lib/settingsFeed";
import { DeveloperView, ErrorState, LoadingState, StatusBadge } from "../shell/ui";
import type { Tone } from "../lib/format";
import { ServiceConfigForm } from "./ServiceConfigForm";
import { FormDraftScope } from "../panel/InteractionFrame";
import { ActionButton, type RunAction } from "../panel/blocks";
import type { PanelAction, PanelField } from "../panel/spec";
import { CommandTrail } from "../pages/CommandTrail";
import {
  connections,
  nestedSecrets,
  type NestedSecret,
  ordinaryForm,
  configurationValues,
  changedFormValues,
  checkLists,
  choiceMaps,
  modelFields,
  modelNames,
  NAMELESS_MODEL,
  NO_REGISTRIES,
  type Connections,
  type ConnectionDirectory,
  type ConnectionSettings,
  type Registries,
} from "./api";
import { SecretEditor } from "./SecretEditor";
import { ResetDefaults } from "./ResetDefaults";
import { approvalHref, chatHref, connectionHref, connectionReturnHref, connectionSession, leaveSettings } from "./navigation";
import { useConversationDrawn } from "../lib/conversations";
import {
  firstSettingsDestination,
  resolveDestination,
  settingsDestinations,
  settingsTitle,
  workspaceSettingsDestinations,
  type SettingsDeclaration,
} from "./directory";

import { PanelView } from "../panel/PanelView";
import { ModelReadiness } from "./ModelReadiness";
import { OwnServiceFold } from "./OwnServiceFold";
import { declaredModelCheck } from "./modelSettlement";
import { WorkbenchSetting, workbenchDestinations, type WorkbenchPrefs } from "./WorkbenchSettings";
import "./settings-list.css";
import "./connections.css";

/** Search declarations only; saved answers and credentials are not search data. */
function matchingField(fields: PanelField[], query: string, parents: string[] = []): { label: string; path: string[] } | undefined {
  for (const field of fields) {
    if (field.secret) continue;
    const path = [...parents, field.key];
    if (field.label.toLowerCase().includes(query)) return { label: field.label, path };
    const nested = matchingField(field.item_fields ?? [], query, path);
    if (nested) return nested;
  }
}

/**
 * Which Agent this page belongs to, as the Hub names it, when the Hub frames this page (`OwnerSettings`).
 * Deletion is drawn here only once the Hub has said who it is for; lifecycle controls stay in the Hub above this frame.
 */
function useHubAgent(embedded: boolean) {
  const [agent, setAgent] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    if (!embedded) return;
    const heard = (event: MessageEvent) => {
      const data = event.data as { type?: unknown; id?: unknown; name?: unknown; instance?: unknown } | null;
      if (event.origin !== location.origin || event.source !== parent || data?.type !== "pa.settings.agent") return;
      if (typeof data.id === "string" && typeof data.name === "string") {
        setAgent({ id: data.id, name: data.name });
      }
    };
    window.addEventListener("message", heard);
    return () => window.removeEventListener("message", heard);
  }, [embedded]);
  return agent;
}

export function ConnectionsPage({
  version, onToast, sessionId = "s-owner", panels = [], active = true, embedded = false, workbench, renderServiceSource, openingRead, platformSettings, agentPicker,
}: {
  version: number;
  onToast: (message: string, tone: "good" | "critical") => void;
  sessionId?: string;
  panels?: PanelEntry[];
  active?: boolean;
  embedded?: boolean;
  /** What the workbench keeps for this Agent; without it the page has only the plugins' settings. */
  workbench?: WorkbenchPrefs;
  /** Optional host-owned service choice, colocated with the settings it supplies. */
  renderServiceSource?: (namespace: string, settingTitle: string, namespaces?: string[]) => ReactNode;
  openingRead?: Promise<ConnectionDirectory>;
  platformSettings?: ReactNode;
  agentPicker?: ReactNode;
}) {
  const [data, setData] = useState<Connections>({ connections: [], problems: [] });
  const [directory, setDirectory] = useState<ConnectionDirectory | null>(null);
  const [detailErrors, setDetailErrors] = useState<Record<string, unknown>>({});
  const detailTickets = useRef<Record<string, number>>({});
  const detailControllers = useRef<Record<string, AbortController>>({});
  const openingUsed = useRef(false);
  const [error, setError] = useState<unknown>(null);
  const [reading, setReading] = useState(true);
  const [lagging, setLagging] = useState(false);
  /**
   * The destination asked for, and 「nothing asked for」 as the empty string.
   *
   * Which part of IP 设置 opens by default is a declaration the page reads,
   * not a constant a caller can substitute: four pages used to write
   * `llm_providers` here and mean 「the first entry point」.
   */
  const [requested, setRequested] = useState(() =>
    new URLSearchParams(location.hash.split("?")[1]).get("namespace") ?? "");
  const [scopeView, setScopeView] = useState<SettingsScope>(() => readSettingsScope(location.hash));
  const [requestedScene,setRequestedScene]=useState(()=>new URLSearchParams(location.hash.split("?")[1]).get("scene")||"");
  const [visited, setVisited] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [problemsOpen, setProblemsOpen] = useState<string[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  useContentMotion(contentRef, `${scopeView}:${requested}:${requestedScene}`, active && !reading);
  useEffect(() => { if (contentRef.current) contentRef.current.scrollTop = 0; }, [requested, query]);
  const [fieldTarget, setFieldTarget] = useState<{ namespace: string; path: string[] } | null>(null);
  const [refresh, setRefresh] = useState(0);
  const developer = useContext(DeveloperView);
  const hubAgent = useHubAgent(embedded);
  /**
   * The last submission a declared settings panel opened.
   *
   * Without it every press of a plugin's own 保存 ended at a four-second toast
   * saying the shell had handed the message over -- which is true and is not
   * what was asked (§7.139 ③, the report's D287). One at a time: the panel
   * reloads after each action, and what a person is waiting for is the button
   * they just pressed.
   */
  const [receipt, setReceipt] = useState<{ id: string; label: string } | null>(null);
  const params = new URLSearchParams(location.hash.split("?")[1]);
  const requestId = params.get("request_id");
  const sourceSession = connectionSession(location.hash, "");
  const returnSession = requestId && !sourceSession ? undefined : sourceSession || sessionId;
  // Whether the owner can read the conversation this page was opened from: the way back leads there only if so.
  const drawn = useConversationDrawn(returnSession);
  const returnTo = connectionReturnHref(location.hash, returnSession, undefined, drawn);
  // A way back that is the conversation waits for the answer, which may turn it into the workspace.
  const returnLink = returnSession && drawn === undefined && returnTo === chatHref(returnSession) ? undefined : returnTo;
  const workspaceSettingsHref = (panel: PanelEntry) => {
    const href = workspaceHref(panel, returnSession);
    return href + (href.includes("?") ? "&" : "?") + new URLSearchParams({ from_settings: "true", ...(returnTo ? { source_settings_return: returnTo } : {}) });
  };
  const requestPanel = params.get("request_panel");
  const ownAppearance = workbench?.appearance;
  const destinations = useMemo(() => settingsDestinations(panels,
    ownAppearance === undefined ? [] : workbenchDestinations(ownAppearance)), [panels, ownAppearance]);
  const workspaces = workspaceSettingsDestinations(panels, destinations, directory?.entries.map(row => row.namespace) ?? []);
  const isAgentSetting = (item: typeof destinations[number] | undefined) => Boolean(item && (item.declared.scope === "agent"
    || item.declared.scope !== "capability" && ["model", "preferences", "memory", "rules", "development"].includes(item.declared.group||item.group)));
  const resolved = resolveDestination(destinations, requested);
  const settingsScope = scopeView === "platform" ? "platform" : resolved ? isAgentSetting(destinations.find(item => item.key === resolved)) ? "agent" : "capabilities" : scopeView;
  const entries = settingsScope === "scenes" || settingsScope === "platform" ? [] : destinations.filter(item => isAgentSetting(item) === (settingsScope === "agent") && (item.group!=="development" || developer || workbench?.developer || item.key===resolved));
  const overview = settingsScope === "scenes";
  useEffect(() => {
    if (embedded && active && readSettingsScope(location.hash) !== settingsScope) parent.postMessage({ type: "pa.settings.scope", scope: settingsScope }, location.origin);
  }, [embedded, active, settingsScope]);
  const selected = overview || settingsScope === "platform" ? "" : resolved || firstSettingsDestination(entries) || "";
  const sceneKey = requestedScene;
  const scene = workspaces.find(row => row.panel.workspace?.key === sceneKey) || workspaces[0];
  const unavailableRequest = requested && !resolveDestination(destinations, requested);
  const pickNamespace = (namespace: string, field?: { path: string[] }) => {
    setRequested(namespace);
    setFieldTarget(field ? { namespace, path: field.path } : null);
    // The request this page was opened for travels with every destination, or
    // the way back to it is gone after the first click.
    const request = requestId
      ? `&${new URLSearchParams({ request_id: requestId, ...(requestPanel ? { request_panel: requestPanel } : {}) })}` : "";
    const scope = namespace ? (isAgentSetting(destinations.find(item => item.key === namespace)) ? "agent" : "capabilities") : settingsScope;
    const href = connectionHref(namespace, returnSession, returnTo) + request;
    navigateWorkbench(href + (scope === "capabilities" ? (href.includes("?") ? "&" : "?") + "scope=capabilities" : ""));
  };
  useEffect(() => {
    const changed = () => { const params = new URLSearchParams(location.hash.split("?")[1]); setRequested(params.get("namespace") ?? ""); setRequestedScene(params.get("scene")||""); setScopeView(readSettingsScope(location.hash)); };
    window.addEventListener("hashchange", changed);
    return () => window.removeEventListener("hashchange", changed);
  }, []);
  const chosen = destinations.find(item => item.key === selected);
  const selectedEntries = chosen ? [chosen] : [];
  const selectedKeys = selectedEntries.map(item => item.key);
  const selectedSignature = JSON.stringify(selectedEntries.map(item => [item.key, item.generationId]));
  useEffect(() => {
    if (selectedKeys.length) setVisited(old => [...new Set([...old, ...selectedKeys])]);
  }, [selectedSignature]);
  // A receipt belongs to the block it was pressed in; carrying it to the next
  // one would report another panel's submission under this panel's heading.
  useEffect(() => setReceipt(null), [selected]);
  const reload = useCallback(() => setRefresh(n => n + 1), []);
  // Reads go out from both effects below and can come back in any order. One asked earlier never replaces one
  // asked later: its answer may be from before a write, and a form would go back to a version already moved past.
  const asked = useRef(0);
  const shown = useRef(0);
  const latest = (ticket: number) => {
    if (ticket < shown.current) return false;
    shown.current = ticket;
    return true;
  };
  useEffect(() => {
    if (!active) return;
    let live = true;
    const controller = new AbortController();
    setReading(true);
    const ticket = ++asked.current;
    const read = !openingUsed.current && openingRead ? openingRead.catch(() => connections.directory(controller.signal)) : connections.directory(controller.signal);
    openingUsed.current = true;
    read.then(result => {
      if (!live) return;
      if (latest(ticket)) { setDirectory(result); setError(null); setLagging(false); }
      setReading(false);
    }).catch(failure => { if (live) { setError(failure); setReading(false); } });
    return () => { live = false; controller.abort(); };
  }, [version, refresh, active]);
  // A directory is declarations, not a batch of every saved form. Read only
  // the selected product section; keep visited forms mounted to preserve drafts.
  const readDetails = useCallback((namespaces: string[]) => {
    for (const namespace of namespaces) {
      const ticket = (detailTickets.current[namespace] ?? 0) + 1;
      detailTickets.current[namespace] = ticket;
      detailControllers.current[namespace]?.abort();
      const controller = new AbortController();
      detailControllers.current[namespace] = controller;
      connections.one(namespace, controller.signal).then(result => {
        if (detailTickets.current[namespace] !== ticket || controller.signal.aborted) return;
        if (result.namespace !== namespace) throw new ApiError("设置未读完整", "请重新读取这项设置。", null, 502);
        setData(old => ({ ...old, connections: [...old.connections.filter(item => item.namespace !== namespace), result] }));
        setDetailErrors(old => ({ ...old, [namespace]: null }));
      }).catch(failure => {
        if (!controller.signal.aborted && detailTickets.current[namespace] === ticket) setDetailErrors(old => ({ ...old, [namespace]: failure }));
      });
    }
  }, []);
  useEffect(() => {
    if (!active) return;
    const namespaces = selectedEntries.filter(item => item.namespace).map(item => item.namespace!);
    readDetails(namespaces);
    return () => { for (const namespace of namespaces) {
      detailTickets.current[namespace] = (detailTickets.current[namespace] ?? 0) + 1;
      detailControllers.current[namespace]?.abort();
    } };
  }, [active, selectedSignature, version, refresh, readDetails]);
  useEffect(() => {
    if (!active) return;
    let live = true;
    let quietRead: AbortController | undefined;
    const readQuietly = (namespace?: string | null) => {
      quietRead?.abort();
      const controller = new AbortController();
      quietRead = controller;
      const ticket = ++asked.current;
      connections.directory(controller.signal).then(result => {
        if (!live || !latest(ticket)) return;
        setDirectory(old => old && JSON.stringify(old) === JSON.stringify(result) ? old : result);
        setLagging(false);
      }).catch(() => { if (live && !controller.signal.aborted && ticket > shown.current) setLagging(true); });
      const selectedNamespaces = selectedEntries.filter(item => item.namespace).map(item => item.namespace!);
      readDetails(namespace ? selectedNamespaces.filter(item => item === namespace) : selectedNamespaces);
    };
    const back = () => { if (document.visibilityState === "visible") readQuietly(); };
    const unsubscribe = settingsFeed.subscribe(tick => readQuietly(tick.namespace));
    window.addEventListener("focus", back);
    return () => { live = false; quietRead?.abort(); unsubscribe(); window.removeEventListener("focus", back); };
  }, [active, selectedSignature, readDetails]);
  useEffect(() => {
    if (embedded && (destinations.length || directory || error)) parent.postMessage({ type: "pa.settings.ready" }, location.origin);
  }, [embedded, destinations.length, directory, error]);
  const current = data.connections.find(item => item.namespace === selected);
  // One choice, one place: the default chat model is chosen on the check its
  // page declared, and the form of the namespace holding it leaves it out.
  const modelCheck = useMemo(() => declaredModelCheck(destinations), [destinations]);
  // What is set where the model is chosen -- the default, and the level for supplied models -- is not drawn
  // twice, and so is left out of the form of the namespace holding it.
  const chosenWithTheModel = useMemo(() => modelCheck
    ? [modelCheck.defaultField, ...(modelCheck.suppliedLevelField ? [modelCheck.suppliedLevelField] : [])] : [],
  [modelCheck]);
  // How many of the models the Agent can answer with somebody else supplies, once they have been read: the
  // owner's own connection is folded away while there are some, and open while there are none.
  const [supplied, setSupplied] = useState<number | null>(null);
  const onModels = useCallback((models: CapabilityEntry[]) => setSupplied(models.filter(model =>
    (model.spec?.metadata as Record<string, unknown> | undefined)?.platform === true).length), []);
  const selectedEntry = destinations.find(item => item.key === selected);
  const selectedPanel = selectedEntry?.panelKey;
  const workbenchSetting = workbench && selectedEntry && !selectedEntry.namespace && !selectedPanel ? selectedEntry.key : null;
  const searchTerm = query.trim().toLowerCase();
  const matchedField = (item: typeof destinations[number]) => query.trim() ? matchingField(data.connections.find(row=>row.namespace===item.namespace)?.fields||[],searchTerm)
    || directory?.entries.find(row=>row.namespace===item.namespace)?.search_fields?.find(field=>field.label.toLowerCase().includes(searchTerm)) : undefined;
  const navigationEntries = (query ? destinations : entries).filter(item => !query
    || `${item.title} ${item.note} ${item.owner}`.toLowerCase().includes(searchTerm)
    || matchingField(data.connections.find(row => row.namespace === item.namespace)?.fields ?? [], searchTerm)
    || directory?.entries.find(row => row.namespace === item.namespace)?.search_fields?.some(field => field.label.toLowerCase().includes(searchTerm)));
  const switchScope = (scope: SettingsScope) => {
    if (scope === "platform" && !platformSettings) { navigateWorkbench("/#/settings?scope=platform"); return; }
    setScopeView(scope); setRequested(""); setQuery(""); setFieldTarget(null);
    const href = connectionHref(undefined, returnSession, returnTo) + (requestId ? "&" + new URLSearchParams({request_id:requestId,...(requestPanel?{request_panel:requestPanel}:{})}) : "");
    navigateWorkbench(href + (scope !== "agent" ? "&scope=" + scope : ""));
  };
  const activation = (namespace?: string) => directory?.entries.find(row => row.namespace === namespace)?.activation || directory?.plugin_states?.[namespace||""];
  const connectionLabel = (namespace?: string) => directory?.entries.find(row=>row.namespace===namespace)?.state?.label;
  const activationLabel = (namespace?: string) => (({installed:"已启用",failed:"启用失败",leaving:"正在停用"} as Record<string,string>)[activation(namespace) || ""] || "状态待确认");
  // Deletion stays at the end of the overview; the Hub owns lifecycle controls above this frame.
  const hubGroup = hubAgent && !embedded ? <section className="settings-group connections-run" aria-label="管理">
    <h3 className="settings-group-title">管理</h3>
    <div className="settings-list">
      <AgentDeletion key={hubAgent.id} agent={hubAgent}
        onDeleted={id => parent.postMessage({ type: "pa.settings.agent-deleted", id }, location.origin)} />
    </div>
  </section> : null;
  return <div className={`connections-page${embedded ? " connections-embedded" : ""}`} aria-busy={reading && !destinations.length}>
    {!embedded ? <header className="page-head connections-heading"><h1 className="page-title">设置</h1>
      {returnLink ? <a className="connections-return btn btn-quiet" {...leaveSettings(returnLink)}><Icon name="back" size={16} />返回工作区</a> : null}
    </header> : null}
    {requestId && !sourceSession ? <p className="micro">原会话未记录，请自行选择要继续的对话。</p> : null}
    {requestId && requestPanel ? <a className="btn" {...leaveSettings(`#/panel/${encodeURIComponent(requestPanel)}?${new URLSearchParams({ request_id: requestId, ...(returnSession ? { session_id: returnSession } : {}) })}`)}>返回密钥请求，确认填写结果</a> : null}
    {error ? <ErrorState error={error} onRetry={reload} /> : null}
    {!directory && !error && !destinations.length ? <LoadingState label="正在读取设置目录" /> : null}
    {directory || destinations.length ? <>
      {!embedded ? <SettingsTabs value={settingsScope} onChange={switchScope} /> : null}
      {settingsScope !== "platform" && agentPicker ? <div className="connections-agent-picker">{agentPicker}</div> : null}
      {settingsScope === "platform" ? platformSettings : <div className="connections-layout">
        <aside className="connections-list">
          <label className="connections-search"><span className="visually-hidden">搜索全部设置</span><span className="pa-field-shell"><Icon name="search" size={16}/><input placeholder="搜索设置" value={query} onChange={event=>setQuery(event.target.value)}/><span className="pa-paint-edge" aria-hidden="true"/></span></label>
          <nav className="connections-group-nav connections-direct-nav" aria-label={settingsScope==="scenes"&&!query?"场景列表":"设置项目"}>
            {settingsScope==="scenes"&&!query ? workspaces.map(row=><button type="button" key={row.panel.key} aria-current={scene?.panel.key===row.panel.key?"page":undefined} onClick={()=>navigateWorkbench(connectionHref(undefined,returnSession,returnTo)+"&scope=scenes&scene="+encodeURIComponent(row.panel.workspace?.key||row.panel.key))}><Icon name="clapperboard" size={18}/><span>{row.panel.workspace?.title||row.panel.title}</span></button>)
            : navigationEntries.map(item=><button type="button" key={item.key} aria-label={item.title+(matchedField(item)?" "+matchedField(item)!.label:"")} aria-current={selected===item.key?"page":undefined} onClick={()=>{const field=matchedField(item);setQuery("");pickNamespace(item.key,field);}} title={item.note}>
              {settingsScope==="capabilities" ? <span className="settings-activation" data-state={activation(item.namespace||item.owner)||"unknown"} aria-label={activationLabel(item.namespace||item.owner)}/> : <Icon name="settings" size={17}/>}
              <span>{item.title}{matchedField(item)?<small className="settings-search-field">{matchedField(item)!.label}</small>:settingsScope==="capabilities"&&connectionLabel(item.namespace)?<small className="settings-search-field">{connectionLabel(item.namespace)}</small>:null}</span>
            </button>)}
          </nav>
          {!navigationEntries.length&&settingsScope!=="scenes" ? <p className="small muted">没有找到匹配设置，换一个词试试。</p> : null}
        </aside>
        <div className="connections-content" ref={contentRef}>
          {lagging ? <p role="status" className="settings-notice connections-lagging">暂时没读到最新设置，先显示上次读取的。<button type="button" className="btn btn-quiet" onClick={reload}>重试</button></p> : null}
          {unavailableRequest ? <p role="status" className="settings-notice">原设置入口已不可用。</p> : null}
          {!overview&&!selectedPanel&&!workbenchSetting ? <header className="connections-detail-heading"><h2>{selectedEntry?.title||"暂无设置"}</h2>{settingsScope==="capabilities"&&activation(selectedEntry?.namespace)?<span className="small muted">{activationLabel(selectedEntry?.namespace)}</span>:null}</header> : null}
          {data.problems.length ? <Accordion className="connections-problems" value={problemsOpen} onValueChange={setProblemsOpen} items={[{value:"problems",title:`部分配置状态未读到（${data.problems.length}）`,content:<>{data.problems.map(item=><p key={item.namespace}>{settingsTitle(destinations,item.namespace,"一项设置")}：{item.detail}</p>)}</>}]}/> : null}
          {overview ? scene ? <section className="settings-scene">
            <h2>{scene.panel.workspace?.title||scene.panel.title}</h2>
            <a className="settings-scene-open settings-row" {...leaveSettings(workspaceSettingsHref(scene.panel))}><span><strong>工作方式</strong><span className="small muted">查看并调整这个场景的偏好</span></span><Icon name="arrow" size={16}/></a>
            <h3>关联配置</h3><div className="settings-list">{scene.namespaces.map(namespace=>{const item=destinations.find(row=>row.namespace===namespace)!;return <button className="settings-row" type="button" key={namespace} onClick={()=>pickNamespace(namespace)}><span><strong>{item.title}</strong><span className="small muted">{item.note}</span></span><Icon name="arrow" size={16}/></button>;})}</div>
          </section> : <p className="muted">当前没有场景。</p> : null}
          {settingsScope==="agent"?hubGroup:null}
        {visited.map(namespace => {
          const connection = data.connections.find(item => item.namespace === namespace);
          const entry = destinations.find(item => item.key === namespace);
          if (workbench && entry && !entry.namespace && !entry.panelKey) return <div key={namespace} hidden={!selectedKeys.includes(namespace)}>
            <WorkbenchSetting which={namespace} prefs={workbench} panels={panels} onToast={onToast} />
          </div>;
          // The live declaration owns visibility, even while the previous
          // connections response is retained during a registry refresh.
          if (!connection || !entry?.namespace) return null;
          const detail = <ConnectionDetail connection={connection} stale={Boolean(detailErrors[namespace]) || !active} onToast={onToast} reload={reload}
            title={entry?.title ?? connection.title} declared={entry?.declared ?? {}} headingProvided
            serviceSource={!query && selectedKeys.includes(namespace) && !entry.declared.readiness && entry.declared.platform_supply?.length
              && !selectedEntries.slice(0, selectedEntries.findIndex(item => item.key === namespace)).some(item => item.declared.platform_supply?.some(supply => entry.declared.platform_supply?.some(current => supply.protocol === current.protocol)))
              ? renderServiceSource?.(namespace, entry.title) : undefined}
            chosenElsewhere={modelCheck?.defaultNamespace === namespace ? chosenWithTheModel : undefined}
            focusField={active && selectedKeys.includes(namespace) && !query && fieldTarget?.namespace === namespace ? fieldTarget.path : undefined}
            onFocused={() => setFieldTarget(null)} folded={modelCheck?.namespace === namespace}
            sessionId={returnSession} active={active && selectedKeys.includes(namespace)} />;
          return <div key={namespace} hidden={!selectedKeys.includes(namespace)}>
            {modelCheck?.namespace === namespace ? <>
              {!query && selectedKeys.includes(namespace) ? renderServiceSource?.(namespace, entry.title) : null}
              <ModelReadiness version={version + refresh} editable compact sessionId={returnSession} onModels={onModels}
                check={modelCheck} active={active && selectedKeys.includes(namespace)} onToast={onToast} />
              {/* Somebody asked for a field in here, or for a key to be put in: it has to be where they look. */}
              <OwnServiceFold open={supplied === 0 || (supplied === null && !renderServiceSource)
                || Boolean(requestId) || fieldTarget?.namespace === namespace}>{detail}</OwnServiceFold>
            </> : detail}
          </div>;
        })}
        {selectedPanel ? <div className="connections-panel connections-document"><PanelView panelKey={selectedPanel} heading="h2" presentation="embedded"
          context={returnSession ? { session_id: returnSession } : undefined} onToast={onToast}
          onSubmitted={(id, label) => setReceipt({ id, label })} />
          {receipt ? <CommandTrail key={receipt.id} label={receipt.label} obligationId={receipt.id} onDone={reload}
            ownerMode={!developer} sessionId={returnSession} active={active} /> : null}</div> : null}
        {!overview ? selectedEntries.filter(item => item.namespace && detailErrors[item.namespace]).map(item => <section key={item.key} aria-label={item.title}><ErrorState error={detailErrors[item.namespace!]} onRetry={reload} /></section>) : null}
        {!overview && !current && !selectedPanel && !workbenchSetting && selectedEntry?.namespace && !detailErrors[selectedEntry.namespace] ? <LoadingState label="正在读取这项设置" /> : null}
      </div>
    </div>}</> : null}
  </div>;
}
/** A multi-protocol consumer may still need personal keys for services the platform does not supply. */
function usesOnlyPlatformService(item: ConnectionSettings, declared: SettingsDeclaration): boolean {
  const binding = declared.platform_supply?.length === 1 ? declared.platform_supply[0] : undefined;
  return typeof binding?.protocol === "string" && Boolean(binding.protocol.trim())
    && item.values.platform_enabled === true;
}

/**
 * The line under a generated settings page's name, or nothing.
 *
 * A template over what is declared and stored: which fields the plugin marked
 * required, whether the platform supplies it, whether the owner's own keys are
 * in. It is not a verdict on whether a call would work -- that takes a live
 * check, and a page that runs one says so itself (`readiness`).
 *
 * Ordered by what a person can act on: something still missing, then who
 * supplies the service, then the owner's own keys. The platform comes before
 * the keys because a supplied page needs none, and saying they are missing
 * there contradicted the Agent's own choice of 涟漪提供. A page with nothing
 * to say, or one the runtime could not answer for, gets no line.
 */
function settingsStatus(item: ConnectionSettings | undefined, declared: SettingsDeclaration):
  { label: string; tone: Tone; set: boolean } | null {
  if (!item || item.schema === null || item.secrets === null
      || item.missing_config === null || item.missing_secrets === null) return null;
  const supplied = usesOnlyPlatformService(item, declared);
  const missing = item.missing_config.length + (supplied ? 0 : item.missing_secrets.length);
  if (missing) return { label: `还缺 ${missing} 项`, tone: "warning", set: false };
  if (declared.platform_supply?.length && item.values.platform_enabled === true) {
    const offers = item.values.platform_services;
    const known = offers !== null && typeof offers === "object" && !Array.isArray(offers);
    if (known && !Object.keys(offers).length)
      return { label: "涟漪提供 · 暂不可用", tone: "warning", set: false };
    return { label: "涟漪提供", tone: "neutral", set: true };
  }
  // A key the plugin calls advanced is one most connections never have: it does not say a key is in.
  const credentials = [...item.secrets, ...nestedSecrets(item).filter((secret) => !secret.advanced)];
  if (!credentials.length || credentials.some((secret) => secret.present == null)) return null;
  const present = credentials.some((secret) => secret.present);
  return { label: present ? "已填密钥" : "未填密钥", tone: "neutral", set: present };
}

/**
 * The one line a refused save shows.
 *
 * The server's sentence, with the field's own name in front when the refusal
 * named one. A failure that is not an answer -- the process is away, the
 * body is somebody's HTML -- gets the form's ordinary line instead of those
 * words.
 */
function refusal(failure: unknown, fields: { key: string; label: string }[]): string {
  const answer = ApiError.fromUnknown(failure);
  if (answer.machine) return "这次没有保存成功，输入已保留。";
  // Read at a generation somebody has since moved: the page reads it again
  // (`changedElsewhere`), and what was typed stays on top of what is there now.
  if (changedElsewhere(failure)) return "别处刚改过，已读回最新设置。";
  const label = answer.key ? fields.find((field) => field.key === answer.key)?.label : undefined;
  return label ? `${label}：${answer.detail}` : answer.detail;
}

/** The server's stale check: this page read the namespace before somebody else wrote it. */
function changedElsewhere(failure: unknown): boolean {
  const answer = ApiError.fromUnknown(failure);
  return !answer.machine && answer.code === "configuration_changed";
}

function ConnectionDetail({
  connection,
  stale,
  onToast,
  reload,
  sessionId,
  active,
  title,
  declared,
  serviceSource,
  chosenElsewhere,
  focusField,
  onFocused,
  folded = false,
  headingProvided = false,
}: {
  connection: ConnectionSettings;
  headingProvided?: boolean;
  active: boolean;
  stale: boolean;
  onToast: (message: string, tone: "good" | "critical") => void;
  reload: () => void;
  sessionId?: string;
  /** The name this setting declared for itself. */
  title: string;
  /** Its `settings:` block: whether it reports its own readiness, which row
   *  columns come first, and the second way in, if it declared one. */
  declared: SettingsDeclaration;
  /** Hub-owned service choice belongs beside the preferences it supplies. */
  serviceSource?: ReactNode;
  /** Fields of this namespace chosen by another control on these settings: not drawn, never written here. */
  chosenElsewhere?: string[];
  focusField?: string[];
  /** The search target was revealed; it is not to be revealed again. */
  onFocused?: () => void;
  /** Drawn inside a fold that already names it: the heading is for readers who cannot see the fold. */
  folded?: boolean;
}) {
  const fields = useMemo(() => connection.fields.filter(field => !chosenElsewhere?.includes(field.key)),
    [connection.fields, chosenElsewhere]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [secretsOpen, setSecretsOpen] = useState<string[]>([]);
  const [receipt, setReceipt] = useState<{ id: string; label: string } | null>(
    null,
  );
  // What a person picks a capability by is its declared act, and a model by
  // its name; the registries are only fetched for a setting that said its
  // values are one of those.
  const references = Object.values(declared.review ?? {}).map((entry) =>
    entry && typeof entry === "object" ? (entry as Record<string, unknown>).reference : undefined);
  const wantsRegistries = references.includes("tool") || references.includes("model");
  const [registries, setRegistries] = useState<Registries | null>(
    wantsRegistries ? null : NO_REGISTRIES);
  useEffect(() => {
    if (!wantsRegistries) return;
    let live = true;
    api.capabilities().then((body) => {
      const named = Object.entries(toolIndex(body.registries))
        .filter(([, tool]) => tool.act).map(([key, tool]) => ({ key, label: tool.act }));
      const count = new Map<string, number>();
      for (const item of named) count.set(item.label, (count.get(item.label) ?? 0) + 1);
      const models = body.registries.find((registry) => registry.kind === "models")?.entries ?? [];
      const names = modelNames(models);
      if (live) setRegistries({
        // Two capabilities under one name are told apart by the only other thing known of them.
        capabilities: named.map((item) => (count.get(item.label) ?? 0) > 1
          ? { ...item, label: `${item.label}（${item.key}）` } : item),
        models: models.map((model) => ({ key: model.key, label: names.get(model.key) ?? NAMELESS_MODEL })),
        read: true,
      });
    }).catch(() => { if (live) setRegistries(NO_REGISTRIES); });
    return () => { live = false; };
  }, [wantsRegistries]);
  // One reading of the registries names every kind of control that picks
  // from them: a model choice, a table of capabilities, a checked list.
  const tables = useMemo(
    () => choiceMaps(connection.schema ?? [], connection.values, declared.review, registries ?? NO_REGISTRIES),
    [connection.schema, connection.values, declared.review, registries],
  );
  const checks = useMemo(
    () => checkLists(connection.schema ?? [], connection.values, declared.review, registries ?? NO_REGISTRIES),
    [connection.schema, connection.values, declared.review, registries],
  );
  const tableKeys = useMemo(() => new Set(Object.keys(tables)), [tables]);
  const ordinary = useMemo(
    () =>
      ordinaryForm(
        modelFields(fields, declared, registries ?? NO_REGISTRIES),
        configurationValues(
          connection.schema ?? [],
          connection.values,
          "display",
          tableKeys,
        ),
      ),
    [fields, connection.values, connection.schema, tableKeys, declared, registries],
  );
  // What is on record, as read: a change is a difference from this, and a
  // save re-reads it. Kept from the first render, a field put back to its old
  // value after one save read as 「没有改动」 and never went.
  const baseline = ordinary.values;
  const displayedFields = useMemo(() => {
    const common = declared.common_fields;
    if (!common) return ordinary.fields;
    const rank = (key: string) => common.includes(key) ? common.indexOf(key) : common.length;
    return ordinary.fields.map(field => ({ ...field, advanced: !common.includes(field.key) }))
      .sort((a, b) => rank(a.key) - rank(b.key));
  }, [ordinary.fields, declared.common_fields]);
  // The tables whose rows hold keys. A key goes with its row (taking a row out takes its key), so rows can
  // be taken out and moved; but putting the whole table back to its default would take every key at once,
  // which this page does not offer.
  const keyedTables = new Set(
    connection.fields
      .filter((field) => field.item_fields?.some((item) => item.secret))
      .map((field) => field.key),
  );
  const secrets = nestedSecrets(connection);
  const status = settingsStatus(connection, declared);
  const [setupOpen, setSetupOpen] = useState<string[]>(() => status?.set ? [] : ["setup"]);
  useEffect(() => { setSetupOpen(status?.set ? [] : ["setup"]); }, [status?.set]);
  const supplied = usesOnlyPlatformService(connection, declared);
  // What the owner stored, offered back to its declared default (ruling
  // 2026-09-30): only boxes this page draws, and not a table holding saved
  // keys, which this page does not empty either.
  const written = new Set(connection.written ?? []);
  const resettable = displayedFields.filter((field) => written.has(field.key) && !keyedTables.has(field.key));
  const resetField = async (key: string) => {
    if (busy || stale) return;
    setBusy(`reset:${key}`);
    try {
      await connections.reset(connection.namespace, [key], connection.generation);
      onToast("已改回默认", "good");
      reload();
    } catch (failure) {
      if (changedElsewhere(failure)) reload();
      onToast(refusal(failure, ordinary.fields), "critical");
    } finally {
      setBusy(null);
    }
  };
  // The owner's save (ruling 204): the changed values go straight to the
  // namespace, at the generation they were read at. Not a turn, so nothing
  // to follow and nothing for anyone to approve; the answer is the outcome,
  // and a refusal is one sentence on the form with the draft kept. A stale
  // generation reads the namespace again: the form then shows what is there
  // now with the typing kept on top, and the next press saves at the new one.
  const write = async (_action: PanelAction, params: Record<string, unknown>, id: string) => {
    if (busy || stale) throw new Error("这一页还在读取，稍后再保存。");
    setBusy(id);
    try {
      const { expected_generation: generation, ...typed } = params;
      const values = configurationValues(
        connection.schema ?? [],
        ordinaryForm(fields, changedFormValues(ordinary.fields, typed, baseline)).values,
        "submit",
        tableKeys,
      );
      if (!Object.keys(values).length) throw new Error("没有改动。");
      try {
        await connections.write(connection.namespace, values, generation ?? connection.generation);
      } catch (failure) {
        if (changedElsewhere(failure)) reload();
        throw new Error(refusal(failure, ordinary.fields));
      }
      onToast("已保存", "good");
      reload();
    } finally {
      setBusy(null);
    }
  };
  // A button the plugin declared beside its settings is the plugin's own
  // action, run by the agent: that one is a command, and is followed.
  const run: RunAction = async (action, values, id) => {
    if (busy || stale) return "";
    setBusy(id);
    setError(null);
    try {
      const result = await api.panelAction(connection.panel_key, action, values);
      if (result.obligation_id)
        setReceipt({ id: result.obligation_id, label: action.label });
      onToast("请求已提交，结果与需要确认的事项会继续显示。", "good");
      return result.obligation_id ?? "";
    } catch (failure) {
      setError(failure);
      throw failure;
    } finally {
      setBusy(null);
    }
  };
  const formActions = connection.actions.filter(
    (action) => action.argument_fields?.length || action.pack_fields_as,
  );
  const otherActions = connection.actions.filter(
    (action) => !formActions.includes(action),
  );
  const primarySecrets = (connection.secrets ?? []).filter(
    (secret) => !secret.advanced || secret.required,
  );
  const moreSecrets = (connection.secrets ?? []).filter(
    (secret) => !primarySecrets.includes(secret),
  );
  const rowSecret = (secret: NestedSecret) => (
    <SecretEditor
      key={secret.name}
      namespace={connection.namespace}
      name={secret.name}
      label={secret.label}
      description={secret.description}
      present={secret.present}
      clearable
      onStored={reload}
    />
  );
  const moreRowSecrets = secrets.filter((secret) => secret.advanced);
  return (
    <section className="connection-detail" aria-label={title}>
      <header>
        {!headingProvided ? <h2 className={folded ? "visually-hidden" : undefined}>{title}</h2> : null}
        {stale ? <StatusBadge tone="neutral" label="状态待刷新" />
          : status ? <StatusBadge tone={status.tone} label={status.label} /> : null}
      </header>
      {declared.note ? <p className="connection-intro">{declared.note}</p> : null}
      {serviceSource}
      {!supplied && status?.label === "已填密钥" ? <p className="connection-verification-note">已保存连接信息，尚未验证服务是否可用。</p> : null}
      {declared.setup_steps?.length ? <Accordion value={setupOpen} onValueChange={setSetupOpen}
        items={[{ value: "setup", title: "接入方法与验证", content: <ol>{declared.setup_steps.map((step, index) => <li key={index}>{step}</li>)}</ol> }]} /> : null}
      {declared.presets?.length ? <div className="connection-presets" role="group" aria-label="常用配置预设">{declared.presets.map(preset => <button key={preset.label} className="btn" type="button" disabled={stale || Boolean(busy)} onClick={async () => {
        if (busy || stale) return;
        setBusy(`preset:${preset.label}`);
        try {
          await connections.write(connection.namespace, ordinaryForm(fields, preset.values).values, connection.generation);
          onToast(`已${preset.label}`, "good"); reload();
        } catch (failure) { setError(failure); }
        finally { setBusy(null); }
      }}>{preset.label}</button>)}</div> : null}
      {error ? <ErrorState error={error} /> : null}
      <fieldset disabled={stale || Boolean(busy)} className="connection-fields">
        {/* `version` is what these values were read at. `config_store` moves
            its generation on every write; a save carries it, so a save over a
            newer value is refused and read again, and when it moves the form
            keeps what was typed on top of the new values (runtime §3). */}
        {/* The draft's name, and the reason this page has one at all.
            `FormDraftScope` is empty by default, and this page never set it --
            so a setting somebody had half-typed was gone the moment the tab
            reloaded, while the same half-typed form on a plugin's own page came
            back. That is the drift §5 exists against: one behaviour, both
            pages. The name is the panel the setting belongs to, which is what a
            plugin page's draft is keyed by too. */}
        {registries === null ? <LoadingState /> : ordinary.fields.length ? (
          <FormDraftScope.Provider value={connection.panel_key}>
          <ServiceConfigForm
            block={{
              kind: "form",
              // The readiness check above already heads this form.
              title: declared.readiness ? undefined
                : displayedFields.some(field => !field.advanced || field.required) ? "常用配置" : "配置",
              fields: displayedFields,
              actions: formActions,
            }}
            index={0}
            idPrefix={`connection-${connection.namespace}-main`}
            focusField={focusField}
            onFocused={onFocused}
            reads={[]}
            runAction={run}
            busy={busy}
            initialValues={ordinary.values}
            version={connection.generation ?? undefined}
            write={write}
            primaryListFields={declared.primary_fields ?? undefined}
            choiceMaps={tables}
            checkLists={checks}
            pricingFields={declared.pricing_fields}
          />
          </FormDraftScope.Provider>
        ) : null}
        {keyedTables.size ? (
          <p className="micro">
            删掉一行，它存着的密钥也一起删掉；改了服务商或服务地址，这一行的密钥要重新填。
          </p>
        ) : null}
        {registries === null ? null : (
          <ResetDefaults
            fields={resettable}
            named={Object.fromEntries(Object.entries(checks).map(([key, list]) => [key, list.options]))}
            busy={Boolean(busy)}
            onReset={(key) => { void resetField(key); }}
          />
        )}
        {otherActions.length ? (
          <section className="connection-actions">
            <h3>已提供的操作</h3>
            {otherActions.map((action, index) => (
              <ActionButton
                key={action.key}
                action={action}
                id={`action:${index}`}
                values={{}}
                runAction={run}
                busy={busy}
              />
            ))}
          </section>
        ) : null}
      </fieldset>
      {/* A setting that reports its own readiness has said something stronger
          than 「已保存」 right above this line; saying both would contradict it. */}
      {supplied ? <p className="settings-notice">此服务已选择涟漪提供，无需填写个人密钥。改用自带服务后，可在这里配置个人密钥。</p>
        : connection.secrets === null || connection.schema === null || connection.secrets.length || secrets.length ? <fieldset className="connection-secrets" disabled={stale || Boolean(busy)}>
        <h3>密钥与凭据</h3>
        {connection.secrets === null || connection.schema === null ? (
          <p role="status">暂时无法确认密钥声明与状态，请刷新后再填。</p>
        ) : (
          <>
            {primarySecrets.map((secret) => (
              <SecretEditor
                key={secret.name}
                namespace={connection.namespace}
                name={secret.name}
                label={secret.title || secret.name}
                description={secret.description}
                present={secret.present}
                prefix={secret.prefix}
                clearable
                onStored={reload}
              />
            ))}
            {secrets.filter((secret) => !secret.advanced).map(rowSecret)}
            {moreSecrets.length || moreRowSecrets.length ? (
              <Accordion className="connection-more" forceMount value={secretsOpen} onValueChange={setSecretsOpen}
                items={[{ value: "secrets", title: `高级凭据（${moreSecrets.length + moreRowSecrets.length}）`, content: <>{moreSecrets.map((secret) => (
                  <SecretEditor
                    key={secret.name}
                    namespace={connection.namespace}
                    name={secret.name}
                    label={secret.title || secret.name}
                    description={secret.description}
                    present={secret.present}
                    prefix={secret.prefix}
                    clearable
                    onStored={reload}
                  />
                ))}
                {moreRowSecrets.map(rowSecret)}
                </> }]} />
            ) : null}
          </>
        )}
      </fieldset> : null}
      {receipt ? (
        <CommandTrail
          label={receipt.label}
          obligationId={receipt.id}
          onDone={reload}
          ownerMode
          sessionId={sessionId}
          active={active}
        />
      ) : null}
      {receipt ? (
        <a className="btn" {...leaveSettings(approvalHref(sessionId))}>
          查看待确认事项
        </a>
      ) : null}

    </section>
  );
}
